import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const CHART_HEIGHT = 300;
const MARGIN = { top: 20, right: 30, bottom: 50, left: 60 };

const CLASSIFICATION_BADGES = {
  chaotic: { bg: '#fee2e2', color: '#991b1b', label: 'CHAOTIC' },
  edge_of_chaos: { bg: '#fef3c7', color: '#92400e', label: 'EDGE OF CHAOS' },
  stable: { bg: '#dcfce7', color: '#166534', label: 'STABLE' },
};

const CUMULATIVE_LINE_COLOR = '#2563eb';
const BAR_POSITIVE_COLOR = '#dc2626';
const BAR_NEGATIVE_COLOR = '#16a34a';

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '4px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 8px',
    borderRadius: '9999px',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.025em',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '12px',
  },
  statsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '24px',
    marginBottom: '12px',
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  statLabel: {
    fontSize: '11px',
    color: '#9ca3af',
  },
  statValue: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1f2937',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '12px',
    fontSize: '12px',
    color: '#6b7280',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

function formatDateLabel(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}`;
}

function formatDateFull(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

export default function LyapunovExponentChart({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!data || !data.dates || data.dates.length === 0 || !data.exponents || data.exponents.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 700;
    const width = Math.max(600, containerWidth);
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

    svg.attr('width', width).attr('height', CHART_HEIGHT);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    const n = data.dates.length;

    // -- Scales --
    const x = d3.scaleBand()
      .domain(data.dates)
      .range([0, innerW])
      .padding(0.3);

    // Y scale: symmetric around zero to encompass both exponents and cumulative
    const allValues = [
      ...data.exponents.filter((v) => v != null),
      ...(data.cumulativeExponent || []).filter((v) => v != null),
    ];
    const valMax = d3.max(allValues, (v) => Math.abs(v)) || 0.1;
    const yPadding = valMax * 0.2;

    const y = d3.scaleLinear()
      .domain([-(valMax + yPadding), valMax + yPadding])
      .nice()
      .range([innerH, 0]);

    // -- Background shading: red above zero, green below zero --
    const zeroY = y(0);

    // Red shading above zero (chaotic region)
    g.append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', innerW)
      .attr('height', Math.max(0, zeroY))
      .attr('fill', '#dc2626')
      .attr('fill-opacity', 0.04);

    // Green shading below zero (stable region)
    g.append('rect')
      .attr('x', 0)
      .attr('y', zeroY)
      .attr('width', innerW)
      .attr('height', Math.max(0, innerH - zeroY))
      .attr('fill', '#16a34a')
      .attr('fill-opacity', 0.04);

    // Region labels
    g.append('text')
      .attr('x', innerW - 4)
      .attr('y', 14)
      .attr('text-anchor', 'end')
      .attr('font-size', '9px')
      .attr('fill', '#dc2626')
      .attr('fill-opacity', 0.5)
      .text('Chaotic');

    g.append('text')
      .attr('x', innerW - 4)
      .attr('y', innerH - 6)
      .attr('text-anchor', 'end')
      .attr('font-size', '9px')
      .attr('fill', '#16a34a')
      .attr('fill-opacity', 0.5)
      .text('Stable');

    // -- Grid lines --
    const yTicks = y.ticks(6);
    g.selectAll('.grid-line')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // -- Zero line (prominent) --
    g.append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', zeroY)
      .attr('y2', zeroY)
      .attr('stroke', '#374151')
      .attr('stroke-width', 1.5);

    // Zero label
    g.append('text')
      .attr('x', -6)
      .attr('y', zeroY)
      .attr('dominant-baseline', 'central')
      .attr('text-anchor', 'end')
      .attr('font-size', '9px')
      .attr('fill', '#374151')
      .attr('font-weight', 600)
      .text('0');

    // -- Exponent bars --
    const barWidth = x.bandwidth() * 0.6;
    const barOffset = (x.bandwidth() - barWidth) / 2;

    data.dates.forEach((date, i) => {
      const val = data.exponents[i];
      if (val === null || val === undefined) return;

      const barX = x(date) + barOffset;
      const isPositive = val >= 0;
      const color = isPositive ? BAR_POSITIVE_COLOR : BAR_NEGATIVE_COLOR;

      const barTop = isPositive ? y(val) : zeroY;
      const barBottom = isPositive ? zeroY : y(val);
      const barHeight = Math.max(1, barBottom - barTop);

      g.append('rect')
        .attr('x', barX)
        .attr('y', barTop)
        .attr('width', barWidth)
        .attr('height', barHeight)
        .attr('fill', color)
        .attr('fill-opacity', 0.7)
        .attr('rx', 2);
    });

    // -- Cumulative exponent overlay line --
    if (data.cumulativeExponent && data.cumulativeExponent.length > 0) {
      const cumulPoints = data.dates
        .map((date, i) => ({
          date,
          value: data.cumulativeExponent[i],
        }))
        .filter((d) => d.value !== null && d.value !== undefined);

      if (cumulPoints.length > 1) {
        const lineGen = d3.line()
          .x((d) => x(d.date) + x.bandwidth() / 2)
          .y((d) => y(d.value))
          .curve(d3.curveMonotoneX);

        g.append('path')
          .datum(cumulPoints)
          .attr('d', lineGen)
          .attr('fill', 'none')
          .attr('stroke', CUMULATIVE_LINE_COLOR)
          .attr('stroke-width', 2);

        // Dots on the cumulative line
        cumulPoints.forEach((d) => {
          g.append('circle')
            .attr('cx', x(d.date) + x.bandwidth() / 2)
            .attr('cy', y(d.value))
            .attr('r', 3)
            .attr('fill', CUMULATIVE_LINE_COLOR)
            .attr('stroke', '#fff')
            .attr('stroke-width', 1.5);
        });
      }
    }

    // -- Axes --

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(d3.axisBottom(x).tickSize(0).tickFormat(formatDateLabel))
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .attr('transform', 'rotate(-30)')
      .attr('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.5em');

    // Y axis
    g.append('g')
      .call(
        d3.axisLeft(y)
          .ticks(6)
          .tickSize(0)
          .tickFormat((v) => {
            const sign = v > 0 ? '+' : '';
            return `${sign}${v.toFixed(3)}`;
          })
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // Y axis label
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerH / 2)
      .attr('y', -45)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .text('Lyapunov Exponent');

    // -- Tooltip hover overlay --
    data.dates.forEach((date, i) => {
      g.append('rect')
        .attr('x', x(date))
        .attr('y', 0)
        .attr('width', x.bandwidth())
        .attr('height', innerH)
        .attr('fill', 'transparent')
        .style('cursor', 'pointer')
        .on('mousemove', (event) => {
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            date,
            exponent: data.exponents[i],
            cumulative: data.cumulativeExponent ? data.cumulativeExponent[i] : null,
          });
        })
        .on('mouseleave', () => {
          setTooltip(null);
        });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  // Handle window resize
  useEffect(() => {
    if (!data || !data.dates || data.dates.length === 0) return;

    let resizeTimer;
    const debouncedResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();
        setTooltip(null);
      }, 150);
    };

    window.addEventListener('resize', debouncedResize);
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', debouncedResize);
    };
  }, [data]);

  // Empty state
  if (!data || !data.dates || data.dates.length === 0 || !data.exponents || data.exponents.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.headerRow}>
          <div style={styles.title}>Lyapunov Exponent</div>
        </div>
        <div style={styles.subtitle}>Measuring pricing system sensitivity and chaos</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No Lyapunov exponent data available
        </div>
      </div>
    );
  }

  const classification = data.classification || 'stable';
  const badge = CLASSIFICATION_BADGES[classification] || CLASSIFICATION_BADGES.stable;
  const overallExponent = data.overallExponent;
  const shortTerm = data.divergenceRate ? data.divergenceRate.shortTerm : null;
  const longTerm = data.divergenceRate ? data.divergenceRate.longTerm : null;

  return (
    <div style={styles.card}>
      <div style={styles.headerRow}>
        <div style={styles.title}>Lyapunov Exponent</div>
        <span
          style={{
            ...styles.badge,
            background: badge.bg,
            color: badge.color,
          }}
        >
          {badge.label}
        </span>
        {data.field && (
          <span style={{ fontSize: '11px', color: '#9ca3af' }}>
            ({data.field})
          </span>
        )}
      </div>
      <div style={styles.subtitle}>Measuring pricing system sensitivity and chaos</div>

      {/* Summary stats */}
      <div style={styles.statsRow}>
        {overallExponent != null && (
          <div style={styles.statItem}>
            <span style={styles.statLabel}>Overall Exponent</span>
            <span
              style={{
                ...styles.statValue,
                color: overallExponent > 0 ? '#dc2626' : overallExponent < 0 ? '#16a34a' : '#6b7280',
              }}
            >
              {overallExponent >= 0 ? '+' : ''}{overallExponent.toFixed(4)}
            </span>
          </div>
        )}
        {shortTerm != null && (
          <div style={styles.statItem}>
            <span style={styles.statLabel}>Short-Term (last 5)</span>
            <span
              style={{
                ...styles.statValue,
                color: shortTerm > 0 ? '#dc2626' : shortTerm < 0 ? '#16a34a' : '#6b7280',
              }}
            >
              {shortTerm >= 0 ? '+' : ''}{shortTerm.toFixed(4)}
            </span>
          </div>
        )}
        {longTerm != null && (
          <div style={styles.statItem}>
            <span style={styles.statLabel}>Long-Term (all)</span>
            <span
              style={{
                ...styles.statValue,
                color: longTerm > 0 ? '#dc2626' : longTerm < 0 ? '#16a34a' : '#6b7280',
              }}
            >
              {longTerm >= 0 ? '+' : ''}{longTerm.toFixed(4)}
            </span>
          </div>
        )}
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <div style={{ width: 12, height: 10, background: BAR_POSITIVE_COLOR, borderRadius: 1, opacity: 0.7 }} />
          <span>Positive (chaotic)</span>
        </div>
        <div style={styles.legendItem}>
          <div style={{ width: 12, height: 10, background: BAR_NEGATIVE_COLOR, borderRadius: 1, opacity: 0.7 }} />
          <span>Negative (stable)</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke={CUMULATIVE_LINE_COLOR} strokeWidth="2" />
          </svg>
          <span>Cumulative avg</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke="#374151" strokeWidth="1.5" />
          </svg>
          <span>Zero line</span>
        </div>
      </div>

      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {formatDateFull(tooltip.date)}
          </div>
          {tooltip.exponent != null && (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
              <span style={{ color: '#6b7280' }}>Exponent</span>
              <span
                style={{
                  fontWeight: 600,
                  color: tooltip.exponent > 0 ? BAR_POSITIVE_COLOR : tooltip.exponent < 0 ? BAR_NEGATIVE_COLOR : '#6b7280',
                }}
              >
                {tooltip.exponent >= 0 ? '+' : ''}{tooltip.exponent.toFixed(4)}
              </span>
            </div>
          )}
          {tooltip.cumulative != null && (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
              <span style={{ color: '#6b7280' }}>Cumulative</span>
              <span
                style={{
                  fontWeight: 600,
                  color: CUMULATIVE_LINE_COLOR,
                }}
              >
                {tooltip.cumulative >= 0 ? '+' : ''}{tooltip.cumulative.toFixed(4)}
              </span>
            </div>
          )}
          <div
            style={{
              marginTop: '4px',
              padding: '2px 6px',
              background: tooltip.exponent > 0 ? '#fee2e2' : tooltip.exponent < 0 ? '#dcfce7' : '#f3f4f6',
              color: tooltip.exponent > 0 ? '#991b1b' : tooltip.exponent < 0 ? '#166534' : '#6b7280',
              borderRadius: '4px',
              fontSize: '10px',
              fontWeight: 600,
              textAlign: 'center',
            }}
          >
            {tooltip.exponent > 0 ? 'DIVERGING' : tooltip.exponent < 0 ? 'CONVERGING' : 'NEUTRAL'}
          </div>
        </div>
      )}
    </div>
  );
}
